/* Автомобили клиента в карточке и форма добавления авто (в макете её не было — сделана в его стиле). */
import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CLASSES, num, type Vehicle } from "@okno/shared";
import { api } from "../../lib/api";
import { useSession } from "../../lib/session";
import type { CustomerDetails } from "../../lib/types";
import { Icon } from "../../ui/Icon";
import { Plate } from "../../ui/kit";
import { useToast } from "../../ui/toast";

interface Props {
  customerId: string;
  vehicles: Vehicle[];
  canEdit: boolean;
  queryKey: unknown[];
}

export function ClientVehicles({ customerId, vehicles, canEdit, queryKey }: Props) {
  const [adding, setAdding] = useState(false);
  return (
    <section>
      <div className="row-between">
        <div className="section-title">Автомобили</div>
        {canEdit && !adding ? (
          <button className="btn btn-ghost btn-sm" onClick={() => setAdding(true)}>
            <Icon name="plus" size={16} />
            Добавить авто
          </button>
        ) : null}
      </div>
      <div className="panel">
        <div className="list">
          {vehicles.map((v) => (
            <div key={v.id} className="list-item">
              <Plate plate={v.plate} />
              <div className="grow">
                <div className="cell-main">{(v.make + " " + v.model).trim() || "Авто"}</div>
                <div className="cell-sub">
                  {CLASSES[v.cls - 1] || CLASSES[0]}
                  {v.color ? " · " + v.color.toLowerCase() : ""}
                  {v.year ? " · " + v.year + " г." : ""}
                  {v.mileage ? " · " + num(v.mileage) + " км" : ""}
                </div>
              </div>
            </div>
          ))}
          {!vehicles.length && !adding ? <div className="list-item muted">Автомобилей пока нет</div> : null}
        </div>
        {adding ? <AddVehicleForm customerId={customerId} queryKey={queryKey} onDone={() => setAdding(false)} /> : null}
      </div>
    </section>
  );
}

const YEAR_MIN = 1950;
const YEAR_MAX = 2100;

function AddVehicleForm({ customerId, queryKey, onDone }: { customerId: string; queryKey: unknown[]; onDone: () => void }) {
  const { locationId } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState({ plate: "", make: "", model: "", cls: 1, color: "", year: "" });
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const plate = f.plate.trim().toUpperCase();
    if (plate.length < 2) {
      toast("Укажите госномер", "bad");
      return;
    }
    const year = f.year ? parseInt(f.year, 10) : null;
    if (year !== null && (!Number.isFinite(year) || year < YEAR_MIN || year > YEAR_MAX)) {
      toast("Год выпуска — от " + YEAR_MIN + " до " + YEAR_MAX, "bad");
      return;
    }
    setBusy(true);
    try {
      const v = await api.post<Vehicle>("/locations/" + locationId + "/customers/" + customerId + "/vehicles", {
        plate,
        make: f.make.trim(),
        model: f.model.trim(),
        cls: f.cls,
        color: f.color.trim(),
        year,
      });
      qc.setQueryData<CustomerDetails>(queryKey, (prev) => (prev ? { ...prev, vehicles: [...prev.vehicles, v] } : prev));
      toast("Автомобиль добавлен");
      onDone();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="panel-body stack stack-sm" onSubmit={(e) => void submit(e)} style={{ borderTop: "1px solid var(--line)" }}>
      <div className="form-grid">
        <label className="field">
          <span>Госномер</span>
          <input className="input" value={f.plate} onChange={(e) => set("plate", e.target.value)} placeholder="123 ABC 02" maxLength={20} autoFocus required />
        </label>
        <label className="field">
          <span>Класс</span>
          <select className="select" value={f.cls} onChange={(e) => set("cls", Number(e.target.value))}>
            {CLASSES.map((c, i) => (
              <option key={c} value={i + 1}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Марка</span>
          <input className="input" value={f.make} onChange={(e) => set("make", e.target.value)} placeholder="Toyota" maxLength={60} />
        </label>
        <label className="field">
          <span>Модель</span>
          <input className="input" value={f.model} onChange={(e) => set("model", e.target.value)} placeholder="Camry" maxLength={60} />
        </label>
        <label className="field">
          <span>Цвет</span>
          <input className="input" value={f.color} onChange={(e) => set("color", e.target.value)} placeholder="Белый" maxLength={40} />
        </label>
        <label className="field">
          <span>Год выпуска</span>
          <input className="input" inputMode="numeric" value={f.year} onChange={(e) => set("year", e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="2020" />
        </label>
      </div>
      <div className="row" style={{ justifyContent: "flex-end" }}>
        <button type="button" className="btn" onClick={onDone} disabled={busy}>
          Отмена
        </button>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? "Сохраняем…" : "Добавить"}
        </button>
      </div>
    </form>
  );
}
